import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { AnswerBody, mcqScore, parseAnswer, reviewUnitInfo } from '../components/AttemptReview';
import { downloadAnswerSheet } from '../lib/answerSheet';
import { hasDescriptiveContent } from '../utils/answers';
import type { DescriptiveAnswer, ReviewBundle, ReviewQuestion } from '../types/models';

const REEVAL_WINDOW_DAYS = 7;
const SHOW_INITIAL = 4;

function gradeOf(score: number, total: number): string {
  if (!total) return '';
  const p = (score / total) * 100;
  if (p >= 90) return 'A+';
  if (p >= 80) return 'A';
  if (p >= 70) return 'B+';
  if (p >= 60) return 'B';
  if (p >= 50) return 'C';
  if (p >= 40) return 'D';
  return 'F';
}

/** "4 days 11 hours" / "11 hours" until a deadline. */
function fmtRemaining(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  if (d >= 1) return `${d} day${d === 1 ? '' : 's'} ${h} hour${h === 1 ? '' : 's'}`;
  if (h >= 1) return `${h} hour${h === 1 ? '' : 's'}`;
  return 'under an hour';
}

const isAnswered = (q: ReviewQuestion): boolean => {
  const a = parseAnswer(q.answerJson);
  if (!a) return false;
  if (a.kind === 'mcq') return a.selectedOptionIds.length > 0;
  const d = a as DescriptiveAnswer;
  return (d.parts?.length ? d.parts.some(hasDescriptiveContent) : hasDescriptiveContent(d));
};

/** Marks awarded for a question (MCQs auto-score when not yet graded). */
const scoreOf = (q: ReviewQuestion): number =>
  q.awardedPoints ?? (q.type !== 'Descriptive' ? mcqScore(q) : 0);

/** full / partial / zero bucket for a question's score. */
function bucketOf(q: ReviewQuestion): 'full' | 'partial' | 'zero' {
  const s = scoreOf(q);
  if (q.marks > 0 && s >= q.marks) return 'full';
  if (s <= 0) return 'zero';
  return 'partial';
}

/** Collapse either/or pairs to a single gradable unit (the answered alternative). */
function gradableUnits(questions: ReviewQuestion[]): ReviewQuestion[] {
  const out: ReviewQuestion[] = [];
  const seen = new Set<string>();
  for (const q of questions) {
    if (q.choiceGroupId) {
      if (seen.has(q.choiceGroupId)) continue;
      seen.add(q.choiceGroupId);
      const members = questions.filter(x => x.choiceGroupId === q.choiceGroupId);
      out.push(members.find(isAnswered) ?? members[0]);
    } else {
      out.push(q);
    }
  }
  return out;
}

function StatCard({ value, label, color }: { value: number; label: string; color?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-value" style={color ? { color } : undefined}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

const BUCKET_COLOR: Record<string, string> = {
  full: 'var(--success)', partial: 'var(--warn-soft-fg)', zero: 'var(--danger)',
};

function ResultQuestionCard({ q, num, altLabel }: { q: ReviewQuestion; num: number; altLabel?: 'A' | 'B' }) {
  const [open, setOpen] = useState(false);
  const bucket = bucketOf(q);
  const score = scoreOf(q);
  const pct = q.marks > 0 ? Math.round((score / q.marks) * 100) : 0;
  const color = BUCKET_COLOR[bucket];
  const promptText = q.prompt.replace(/<[^>]+>/g, '').trim();

  return (
    <div className="result-q">
      <button type="button" className="result-q-head" onClick={() => setOpen(o => !o)}>
        <span className={`result-q-num ${bucket}`}>{num}</span>
        <span className="result-q-prompt">
          {altLabel && <span className="result-q-alt">{altLabel}</span>}
          {promptText || <span className="muted">(no prompt)</span>}
        </span>
        <span className="result-q-marks" style={{ color }}>{score} / {q.marks}</span>
        <span className={'caret' + (open ? ' open' : '')} aria-hidden>▾</span>
      </button>

      {open && (
        <div className="result-q-answer">
          <div className="result-q-label">Your answer</div>
          <AnswerBody question={q} />
        </div>
      )}

      <div className="result-q-bar">
        <div className="result-q-bar-fill" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

export function StudentResultPage() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [bundle, setBundle] = useState<ReviewBundle | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Re-evaluation request UI.
  const [showForm, setShowForm] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showAllQuestions, setShowAll] = useState(false);

  function load() {
    api.getMyResult(examId!).then(setBundle).catch(e => setError(e.message));
  }
  useEffect(load, [examId]);

  const units = useMemo(() => bundle ? gradableUnits(bundle.questions) : [], [bundle]);
  const unitInfo = useMemo(() => bundle ? reviewUnitInfo(bundle.questions) : new Map(), [bundle]);

  if (error) return <div className="container"><div className="card error">{error}</div></div>;
  if (!bundle) return <div className="container"><div className="card muted">Loading result…</div></div>;

  const b = bundle;
  const disqualified = b.status === 'Disqualified';
  const score = b.scoreAwarded ?? units.reduce((s, q) => s + scoreOf(q), 0);
  const pct = b.totalMarks ? Math.round((score / b.totalMarks) * 100) : 0;
  const grade = gradeOf(score, b.totalMarks);
  const pass = !disqualified && pct >= 40;

  const full = units.filter(q => bucketOf(q) === 'full').length;
  const partial = units.filter(q => bucketOf(q) === 'partial').length;
  const zero = units.filter(q => bucketOf(q) === 'zero').length;

  const subtitle = [b.subjectCode, b.examTitle, b.batchName].filter(Boolean).join(' · ');
  const heading = b.subjectName || b.examTitle;
  const publishedOn = b.resultPublishAt
    ? new Date(b.resultPublishAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  // Re-evaluation window (7 days from publication).
  const deadlineMs = b.resultPublishAt ? new Date(b.resultPublishAt).getTime() + REEVAL_WINDOW_DAYS * 86400000 : null;
  const windowMs = deadlineMs ? deadlineMs - Date.now() : null;
  const windowClosed = windowMs != null && windowMs <= 0;

  // Questions a student might contest = the ones that lost marks.
  const contestable = units.filter(q => scoreOf(q) < q.marks);

  const togglePick = (id: string) =>
    setPicked(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  async function submitReeval() {
    const chosen = contestable.filter(q => picked.has(q.id));
    if (chosen.length === 0 && !reason.trim()) {
      alert('Select at least one question or describe why you want a re-evaluation.');
      return;
    }
    const labels = chosen.map(q => `Q${(unitInfo.get(q.id)?.unit ?? 0)}`).join(', ');
    const combined = [labels && `Re-evaluation requested for: ${labels}.`, reason.trim()]
      .filter(Boolean).join('\n');
    setSubmitting(true);
    try {
      await api.requestReevaluation(examId!, combined || 'Re-evaluation requested.');
      setShowForm(false);
      setReason('');
      setPicked(new Set());
      load();
    } catch (e: any) {
      alert(e?.message ?? 'Failed to submit re-evaluation request.');
    } finally {
      setSubmitting(false);
    }
  }

  const visible = showAllQuestions ? units : units.slice(0, SHOW_INITIAL);

  return (
    <div className="student-shell">
      <header className="result-topbar">
        <button className="topbar-ghost" onClick={() => nav('/exams')}>← Back to dashboard</button>
        <div className="result-topbar-title">
          <strong>Result</strong>
          <span className="muted">{subtitle}</span>
        </div>
        <div className="row" style={{ gap: 8, marginLeft: 'auto' }}>
          <button onClick={() => downloadAnswerSheet(b)}
                  title="Download your answer sheet (your answers only) as a PDF">
            ⬇ Download answer sheet
          </button>
          <button onClick={() => window.print()}>⬇ Download mark sheet</button>
        </div>
      </header>

      <main className="student-main">
        {/* Header / score */}
        <div className="result-header">
          <div>
            <div className="result-header-sub">{subtitle}</div>
            <h1 className="result-header-title">{heading}</h1>
            <div className="muted" style={{ fontSize: 13 }}>
              Evaluated by staff{publishedOn ? ` · Published ${publishedOn}` : ''}
            </div>
            <span className={`exam-badge ${pass ? 'result' : 'fail'}`} style={{ marginTop: 12, display: 'inline-flex' }}>
              {disqualified ? 'Disqualified' : pass ? '✓ Pass' : '✗ Fail'}
            </span>
          </div>
          <div className="result-score">
            {disqualified ? (
              <div className="result-score-big" style={{ color: 'var(--danger)' }}>—</div>
            ) : (
              <>
                <div className="result-score-big">{score}</div>
                <div className="muted">out of {b.totalMarks}</div>
                <div className={pass ? 'score-pass' : 'score-fail'}>{pct}% · {grade}</div>
              </>
            )}
          </div>
        </div>

        {/* Stat cards */}
        {!disqualified && (
          <div className="dash-stats">
            <StatCard value={units.length} label="Questions" />
            <StatCard value={full} label="Full marks" color="var(--success)" />
            <StatCard value={partial} label="Partial marks" color="var(--warn-soft-fg)" />
            <StatCard value={zero} label="Zero marks" color="var(--danger)" />
          </div>
        )}

        {b.graderNotes && (
          <div className="card" style={{ marginBottom: 22 }}>
            <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Examiner remarks</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{b.graderNotes}</div>
          </div>
        )}

        {/* Question-wise marks */}
        <section className="dash-section">
          <h2>Question-wise marks</h2>
          <div className="result-q-list">
            {visible.map(q => (
              <ResultQuestionCard key={q.id} q={q}
                num={unitInfo.get(q.id)?.unit ?? 1}
                altLabel={unitInfo.get(q.id)?.alt} />
            ))}
          </div>
          {units.length > SHOW_INITIAL && (
            <button className="result-showall" onClick={() => setShowAll(s => !s)}>
              {showAllQuestions ? 'Show fewer questions' : `Show all ${units.length} questions`}
            </button>
          )}
        </section>

        {/* Re-evaluation */}
        <section className="dash-section">
          <h2>Re-evaluation</h2>

          {b.reevaluationStatus === 'Requested' && (
            <div className="card"><div className="muted">
              Your re-evaluation request has been submitted and is awaiting review.
            </div></div>
          )}
          {b.reevaluationStatus === 'Completed' && (
            <div className="card"><div className="success">
              Your re-evaluation has been completed. The score above is final.
            </div></div>
          )}

          {b.reevaluationStatus === 'None' && !showForm && (
            <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div className="muted">
                {windowClosed
                  ? 'The re-evaluation window for this result has closed.'
                  : 'If you believe an answer was marked incorrectly, you can request a re-evaluation.'}
              </div>
              <button className="primary" disabled={windowClosed} onClick={() => setShowForm(true)}>
                Request re-evaluation
              </button>
            </div>
          )}

          {b.reevaluationStatus === 'None' && showForm && (
            <div className="card">
              <h3 style={{ marginTop: 0 }}>Request re-evaluation</h3>
              <div className="muted" style={{ marginBottom: 14 }}>
                Select questions you believe were marked incorrectly. Requests must be submitted within
                {' '}{REEVAL_WINDOW_DAYS} days of result publication. Results may go up or down after re-evaluation.
              </div>

              <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Select questions</div>
              <div className="reeval-picks">
                {contestable.length === 0 && <div className="muted">You scored full marks on every question.</div>}
                {contestable.map(q => {
                  const n = unitInfo.get(q.id)?.unit ?? 0;
                  const label = q.prompt.replace(/<[^>]+>/g, '').trim().slice(0, 48);
                  return (
                    <label key={q.id} className="reeval-pick">
                      <input type="checkbox" checked={picked.has(q.id)} onChange={() => togglePick(q.id)} />
                      <span>Q{n} — {label || 'Question'}</span>
                      <span className="muted">{scoreOf(q)} / {q.marks}</span>
                    </label>
                  );
                })}
              </div>

              <label style={{ display: 'block', marginTop: 14 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Reason (optional)</div>
                <textarea rows={4} value={reason} onChange={e => setReason(e.target.value)}
                          placeholder="Explain why you believe these questions deserve re-evaluation…" />
              </label>

              {windowMs != null && (
                <div className="muted" style={{ fontSize: 13, margin: '12px 0' }}>
                  ⏳ Re-evaluation window closes in <strong>{fmtRemaining(windowMs)}</strong>
                </div>
              )}

              <div className="row" style={{ gap: 8 }}>
                <button className="primary" onClick={submitReeval} disabled={submitting || windowClosed}>
                  {submitting ? 'Submitting…' : '✓ Submit request'}
                </button>
                <button onClick={() => { setShowForm(false); setReason(''); setPicked(new Set()); }} disabled={submitting}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
